import React, { useState } from 'react';
import api from '../lib/api';

// Shared transcript actions — Copy · TXT · SRT · VTT.
//
// Used by the Raw Footage clip panel and the Video Review transcript panel so
// both screens offer exactly the same set.
//
// The downloads go through the authed axios client rather than a plain <a href>:
// the API is a different origin (api.heliconflow.app) behind Bearer auth, so a
// bare link resolves against the SPA host and never reaches the endpoint.

function saveBlob(data, filename, mime) {
  const url = URL.createObjectURL(new Blob([data], { type: mime }));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

const FORMATS = [
  { key: 'txt', label: 'TXT', mime: 'text/plain;charset=utf-8' },
  { key: 'srt', label: 'SRT', mime: 'application/x-subrip;charset=utf-8' },
  { key: 'vtt', label: 'VTT', mime: 'text/vtt;charset=utf-8' },
];

export default function TranscriptDownloads({ transcriptionId, transcriptText, baseName, buttonStyle }) {
  const [busy, setBusy]     = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError]   = useState(null);

  const btn = { fontSize: 11, padding: '4px 10px', ...(buttonStyle || {}) };

  // Strip the media extension so transcript files sit next to the clip:
  // "C2049.MP4" → "C2049.srt".
  const stem = (baseName || '').replace(/\.[^.]+$/, '') || `transcript_${String(transcriptionId).slice(0, 8)}`;

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(transcriptText || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError('Copy failed');
    }
  }

  async function handleDownload(fmt) {
    setBusy(fmt.key);
    setError(null);
    try {
      const res = await api.get(`/api/transcriptions/${transcriptionId}/${fmt.key}`, { responseType: 'blob' });
      saveBlob(res.data, `${stem}.${fmt.key}`, fmt.mime);
    } catch (err) {
      setError(err?.response?.status === 404 ? 'Transcript not ready' : 'Download failed');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <button className="btn-ghost" style={btn} onClick={handleCopy}>
        {copied ? 'Copied' : 'Copy'}
      </button>
      {FORMATS.map(f => (
        <button
          key={f.key}
          className="btn-ghost"
          style={btn}
          disabled={busy === f.key || !transcriptionId}
          onClick={() => handleDownload(f)}
          title={`Download the transcript as ${f.label}`}
        >
          {busy === f.key ? '…' : f.label}
        </button>
      ))}
      {error && <span style={{ fontSize: 11, color: 'var(--s-incomplete-t)' }}>{error}</span>}
    </div>
  );
}
